import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';

export const useSort = (defaultKey = null, defaultDirection = 'asc') => {
  const [sortConfig, setSortConfig] = useState({ key: defaultKey, direction: defaultDirection });

  const requestSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const sortItems = useCallback((items) => {
    if (!sortConfig.key || !items?.length) return items;

    return [...items].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const aNum = typeof aVal === 'number' ? aVal : parseFloat(aVal);
      const bNum = typeof bVal === 'number' ? bVal : parseFloat(bVal);

      let comparison;
      if (!isNaN(aNum) && !isNaN(bNum) && typeof aVal !== 'boolean') {
        comparison = aNum - bNum;
      } else {
        comparison = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [sortConfig]);

  return { sortConfig, requestSort, sortItems };
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort, align = 'left', sticky = false }) => {
  const isActive = sortConfig.key === sortKey;
  const alignClass = align === 'right' ? 'text-right justify-end' : 'text-left';
  const stickyClass = sticky ? 'sticky top-0 z-10 bg-gray-50' : '';

  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none transition-colors ${alignClass} ${stickyClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {isActive ? (
          sortConfig.direction === 'asc'
            ? <ChevronUp className="h-3.5 w-3.5 text-yellow-600" />
            : <ChevronDown className="h-3.5 w-3.5 text-yellow-600" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </span>
    </th>
  );
};

export default SortableHeader;
